import {
  ChangeEvent,
  createContext,
  KeyboardEvent,
  ReactNode,
  RefObject,
  use,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import styled from 'styled-components';
import CloseCircleIcon from '../assets/close-circle.svg?react';
import SearchIcon from '../assets/search.svg?react';

import { isMobile } from './billing.ts';
import { headerHeight } from './shared.tsx';

// NOTE: This context must be provided by the parent component
export const SearchContext = createContext<{
  searchString: string;
  setSearchString: (value: string) => void;
  searchHasFocus: boolean;
  setSearchHasFocus: (value: boolean) => void;
  searchInputRef: RefObject<HTMLInputElement | null>;
}>({} as any);

export const SearchProvider = ({ children }: { children: ReactNode }) => {
  const [searchString, setSearchString] = useState('');
  const [searchHasFocus, setSearchHasFocus] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);
  return (
    <SearchContext value={{ searchString, setSearchString, searchHasFocus, setSearchHasFocus, searchInputRef }}>
      {children}
    </SearchContext>
  );
};

export function matches({ value, searchString }: { value: string; searchString: string }): boolean {
  if (!searchString) {
    return true;
  }
  for (const searchTerm of searchString.toLowerCase().split(' ')) {
    let found = false;
    for (const valueTerm of value.toLowerCase().split(' ')) {
      if (valueTerm.startsWith(searchTerm)) {
        found = true;
      }
    }
    if (!found) {
      return false;
    }
  }
  return true;
}

export function SearchControl({
  selectContent,
  onKeyDown,
  onKeyUp,
  onCancel,
  placeholder,
  retainFocusOnCancel,
}: {
  onCancel: () => void;
  onKeyDown?: (event: KeyboardEvent<HTMLInputElement>) => void;
  onKeyUp?: (event: KeyboardEvent<HTMLInputElement>) => void;
  selectContent?: boolean;
  placeholder?: string;
  retainFocusOnCancel?: boolean;
}) {
  const { searchString, setSearchString, setSearchHasFocus, searchInputRef } = use(SearchContext);

  const onSearchFocus = useCallback(() => {
    setSearchHasFocus?.(true);
  }, [setSearchHasFocus]);

  const onSearchBlur = useCallback(() => {
    // this delay allows search results to be tapped before the footer & minimized player become visible
    setTimeout(() => setSearchHasFocus?.(false), 50);
  }, [setSearchHasFocus]);

  const onSearchChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setSearchString(event.target.value);
    },
    [setSearchString],
  ); // suppress DOM warning

  const cancelSearch = useCallback(() => {
    setSearchString('');
    if (retainFocusOnCancel) {
      searchInputRef.current?.focus();
    } else {
      searchInputRef.current?.blur();
    }
    onCancel?.();
  }, [onCancel, retainFocusOnCancel, searchInputRef, setSearchString]);

  const onSearchKeyUp = useCallback(
    (event: KeyboardEvent<HTMLInputElement>) => {
      onKeyUp?.(event);
      if (event.key === 'Escape') {
        cancelSearch();
        searchInputRef.current?.blur();
      }
    },
    [cancelSearch, onKeyUp, searchInputRef],
  );

  useEffect(() => {
    if (selectContent) {
      searchInputRef.current?.select();
    }
    searchInputRef.current?.focus();
  }, [searchInputRef, selectContent]);

  const onSubmitSearch = useCallback(
    (event: any) => {
      event.preventDefault();
      event.stopPropagation();
      searchInputRef.current?.blur();
    },
    [searchInputRef],
  );

  // iOS sends an Enter key when the blue search button is pressed on the soft keyboard - can also wrap in a form
  const onKeyDownLocal = useCallback(
    (event: any) => {
      if (event.key === 'Enter' && isMobile()) {
        onSubmitSearch(event);
        return;
      }
      onKeyDown?.(event);
    },
    [onKeyDown, onSubmitSearch],
  );

  // TODO(hewitt): remove $visible and $fullWidth properties

  return (
    <OuterSearchWrapper key="search">
      <InnerSearchWrapper $fullWidth={true}>
        <SearchIconCell key="searchIcon">
          <SearchIconWrapper>
            <SearchIcon />
          </SearchIconWrapper>
        </SearchIconCell>
        <SearchCell
          key="search"
          id="song-search-bar"
          type="search"
          enterKeyHint="search"
          placeholder={placeholder ?? 'Search'}
          onFocus={onSearchFocus}
          onBlur={onSearchBlur}
          onKeyUp={onSearchKeyUp}
          onKeyDown={onKeyDownLocal}
          autoComplete="off"
          data-1p-ignore /* disable password manager on search control */
          data-lpignore="true" /* disable password manager on search control */
          value={searchString}
          ref={searchInputRef}
          onChange={onSearchChange}
        />
        <SearchCancel key="cancelSearch" onClick={cancelSearch}>
          <CloseCircleIcon className="cancelSearchIcon" />
        </SearchCancel>
      </InnerSearchWrapper>
    </OuterSearchWrapper>
  );
}

const OuterSearchWrapper = styled.div`
  display: flex;
  align-items: center;
  padding-top: 10px;
  padding-bottom: 7px;
  text-align: center;
  height: ${headerHeight};
  width: 100%;
  border-bottom: 1px solid transparent;
`;

const InnerSearchWrapper = styled.div<{ $fullWidth: boolean }>`
  display: flex;
  align-items: center;
  background-color: var(--color-background-search);
  border-radius: 20px;
  box-sizing: border-box;
  height: 100%;
  width: 100%;
  margin-left: ${(props) => (props.$fullWidth ? '15px' : '0')};
  margin-right: ${(props) => (props.$fullWidth ? '10px' : '0')};
`;

const SearchIconCell = styled.div`
  width: 30px;
  margin: 0 4px;
`;

const SearchIconWrapper = styled.div`
  width: 25px;
  padding-left: 8px;
  padding-top: 1px;
  fill: var(--color-text-search-placeholder);
`;

const SearchCell = styled.input`
  width: 100%;
  border: none;
  background-color: transparent;
  font-size: medium;
  box-sizing: border-box;

  &:focus {
    outline: 0 none;
    color: var(--color-text);
  }

  &::placeholder {
    color: var(--color-text-search-placeholder);
  }
`;

const SearchCancel = styled.div`
  padding-left: 8px;
  padding-right: 3px;
`;
